import { TTrackTypes } from "@/types";

/**
 * Centralized track configuration for state management operations.
 *
 * Maps each track type to its corresponding state key and reducer action type,
 * along with behavioral flags that control how cart sync operations work.
 *
 * Used by:
 *   - TrackProvider (state/TrackContext.tsx): cart:updated event handler
 *   - cartOperations.ts: updateShopifyCart track-specific dispatch
 *   - ProductSelectorApp.tsx: incoming variant selection dispatch
 *
 * Adding a new track type:
 *   1. Add an entry here with the correct stateKey and action type
 *   2. Add the corresponding reducer case in state/trackReducer.ts
 *   3. Add the state shape in state/trackInitialState.ts / types/index.ts
 */

type TrackSelectionStateKey =
  | "mainTrackSelection"
  | "miniTrackSection"
  | "juniorTrackSelection"
  | "intermediateTrackSelection"
  | "battleKartSelection";

export interface TrackVariantConfig {
  trackType: TTrackTypes;
  stateKey: TrackSelectionStateKey;
  setVariantAction: string;
  /**
   * Controls behavior when cart items exist but no variants match this track.
   *
   * true  → dispatch with empty selectedVariants (clears the track's selection)
   * false → skip dispatch entirely (preserves the user's in-progress selection)
   *
   * Main track is false so users don't lose their selection when unrelated
   * cart items from other tracks are removed.
   */
  dispatchWhenFilteredEmpty: boolean;
  /**
   * Controls behavior when the entire cart becomes empty.
   *
   * true  → clear this track's selected variants
   * false → preserve the selection (main track keeps selections so users
   *         don't lose work when the cart is cleared)
   */
  clearWhenCartEmpty: boolean;
}

export const TRACK_VARIANT_CONFIGS: TrackVariantConfig[] = [
  {
    trackType: "BATTLE_KART_TRACK",
    stateKey: "battleKartSelection",
    setVariantAction: "SET_DATE_AND_VARIANT_BATTLE_KART_TRACK",
    dispatchWhenFilteredEmpty: true,
    clearWhenCartEmpty: true,
  },
  {
    trackType: "MAIN_TRACK",
    stateKey: "mainTrackSelection",
    setVariantAction: "SET_DATE_AND_VARIANT",
    dispatchWhenFilteredEmpty: false,
    clearWhenCartEmpty: false,
  },
  {
    trackType: "MINI_TRACK",
    stateKey: "miniTrackSection",
    setVariantAction: "SET_DATE_AND_VARIANT_MINI_TRACK",
    dispatchWhenFilteredEmpty: true,
    clearWhenCartEmpty: true,
  },
  {
    trackType: "JUNIOR_TRACK",
    stateKey: "juniorTrackSelection",
    setVariantAction: "SET_DATE_AND_VARIANT_JUNIOR_TRACK",
    dispatchWhenFilteredEmpty: true,
    clearWhenCartEmpty: true,
  },
  {
    trackType: "INTERMEDIATE_TRACK",
    stateKey: "intermediateTrackSelection",
    setVariantAction: "SET_DATE_AND_VARIANT_INTERMEDIATE_TRACK",
    dispatchWhenFilteredEmpty: true,
    clearWhenCartEmpty: true,
  },
];

/**
 * Quick lookup: track type → reducer action type for setting date/variant.
 *
 * Example: TRACK_TO_VARIANT_ACTION["MINI_TRACK"] → "SET_DATE_AND_VARIANT_MINI_TRACK"
 */
export const TRACK_TO_VARIANT_ACTION: Record<string, string> = Object.fromEntries(
  TRACK_VARIANT_CONFIGS.map((c) => [c.trackType, c.setVariantAction]),
);
